import types from '../../../mutation-types';
import {
  sanitizeConversationListUnreadForViewer,
  shouldPreserveAssignedUnreadForBox,
} from 'dashboard/helper/unreadOwnership';

export const MAX_MESSAGE_PREFETCH_CONVERSATIONS = 6;
export const DEFAULT_MESSAGE_PREFETCH_CONVERSATIONS =
  MAX_MESSAGE_PREFETCH_CONVERSATIONS;
export const INITIAL_MESSAGE_PREFETCH_CONVERSATIONS = 1;
// Detalhes só devem ser aquecidos para a conversa aberta. A lista e as demais
// abas continuam carregando sob demanda, evitando rajadas no boot.
export const MAX_DETAILS_PREFETCH_CONVERSATIONS = 1;

export const normalizeConversationPrefetchLimit = (
  value,
  fallback = DEFAULT_MESSAGE_PREFETCH_CONVERSATIONS
) => {
  const parsed = Number.parseInt(value, 10);

  if (Number.isNaN(parsed)) {
    return fallback;
  }

  if (parsed < 0) {
    return 0;
  }

  return Math.min(parsed, MAX_MESSAGE_PREFETCH_CONVERSATIONS);
};

const getGlobalPrefetchConversationLimit = () => {
  const configLimit =
    typeof window === 'undefined'
      ? undefined
      : window.chatwootConfig?.conversationPrefetchLimit;

  return normalizeConversationPrefetchLimit(configLimit);
};

export const getPrefetchConversationLimit = currentUser => {
  const userLimit = currentUser?.ui_settings?.conversation_prefetch_limit;
  const fallback = getGlobalPrefetchConversationLimit();

  if (userLimit !== undefined && userLimit !== null) {
    return normalizeConversationPrefetchLimit(userLimit, fallback);
  }

  return fallback;
};

export const getConversationDetailsPrefetchLimit = currentUser => {
  return Math.min(
    getPrefetchConversationLimit(currentUser),
    MAX_DETAILS_PREFETCH_CONVERSATIONS
  );
};

export const isSmallViewport = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (typeof window.matchMedia === 'function') {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  return window.innerWidth < 768;
};

const getConversationRecency = conversation => {
  const recency =
    conversation?.last_activity_at ??
    conversation?.timestamp ??
    conversation?.updated_at ??
    conversation?.created_at ??
    0;
  const parsedRecency = Number(recency);

  if (Number.isFinite(parsedRecency)) return parsedRecency;

  if (typeof recency === 'string' && recency.trim()) {
    const parsedDate = Date.parse(recency);
    if (Number.isFinite(parsedDate)) return parsedDate;
  }

  return 0;
};

const getConversationId = conversation => {
  const parsedId = Number(conversation?.id);
  return Number.isFinite(parsedId) ? parsedId : 0;
};

const getConversationLoadProgress = conversation => {
  if (
    conversation?.dataFetched === true ||
    conversation?.allMessagesLoaded === true
  ) {
    return 100;
  }

  if (
    conversation?.prefetchProgress !== undefined &&
    conversation?.prefetchProgress !== null
  ) {
    const parsedProgress = Number(conversation.prefetchProgress);

    if (!Number.isNaN(parsedProgress)) {
      return Math.max(0, Math.min(parsedProgress, 100));
    }
  }

  return 0;
};

const getConversationBeforeMessageId = conversation => {
  const messages = Array.isArray(conversation?.messages)
    ? conversation.messages
    : [];

  return messages[0]?.id;
};

export const buildConversationPrefetchPlan = ({
  conversations = [],
  limit,
  selectedChatId,
  excludedConversationIds = [],
}) => {
  const conversationLimit = normalizeConversationPrefetchLimit(limit);

  if (conversationLimit <= 0) {
    return {
      candidateConversations: [],
      targets: [],
      candidateCount: 0,
      progress: 0,
      limit: 0,
    };
  }

  const selectedConversationId = Number(selectedChatId);
  const excludedIds = new Set(
    (excludedConversationIds || [])
      .map(conversationId => Number(conversationId))
      .filter(conversationId => Number.isFinite(conversationId))
  );
  const sortedConversations = [...conversations].sort(
    (a, b) =>
      getConversationRecency(b) - getConversationRecency(a) ||
      getConversationId(b) - getConversationId(a)
  );
  const seenConversationIds = new Set();
  const candidateConversations = [];
  const targets = [];

  for (let index = 0; index < sortedConversations.length; index += 1) {
    const conversation = sortedConversations[index];
    const conversationId = Number(conversation?.id);
    const beforeMessageId = getConversationBeforeMessageId(conversation);

    if (
      conversationId &&
      conversationId !== selectedConversationId &&
      !seenConversationIds.has(conversationId)
    ) {
      const target = {
        conversationId,
        beforeMessageId,
        conversation,
      };

      seenConversationIds.add(conversationId);

      if (candidateConversations.length < conversationLimit) {
        candidateConversations.push(target);
      }

      if (
        !excludedIds.has(conversationId) &&
        targets.length < conversationLimit
      ) {
        targets.push(target);
      }

      if (
        candidateConversations.length >= conversationLimit &&
        targets.length >= conversationLimit
      ) {
        break;
      }
    }
  }

  const progress = candidateConversations.length
    ? Math.round(
        candidateConversations.reduce((sum, item) => {
          return sum + getConversationLoadProgress(item.conversation);
        }, 0) / candidateConversations.length
      )
    : 0;

  return {
    candidateConversations,
    targets,
    candidateCount: candidateConversations.length,
    progress,
    limit: conversationLimit,
  };
};

export const setPageFilter = ({ dispatch, filter, page, markEndReached }) => {
  dispatch('conversationPage/setCurrentPage', { filter, page }, { root: true });
  if (markEndReached) {
    dispatch('conversationPage/setEndReached', { filter }, { root: true });
  }
};

export const setContacts = (commit, chatList) => {
  commit(
    `contacts/${types.SET_CONTACTS}`,
    chatList.map(chat => chat.meta.sender)
  );
};

export const isOnMentionsView = ({ route: { name: routeName } }) => {
  const MENTION_ROUTES = [
    'conversation_mentions',
    'conversation_through_mentions',
  ];
  return MENTION_ROUTES.includes(routeName);
};

export const isOnUnattendedView = ({ route: { name: routeName } }) => {
  const UNATTENDED_ROUTES = [
    'conversation_unattended',
    'conversation_through_unattended',
  ];
  return UNATTENDED_ROUTES.includes(routeName);
};

export const isOnParticipatingView = ({ route: { name: routeName } }) => {
  const PARTICIPATING_ROUTES = [
    'conversation_participating',
    'conversation_through_participating',
  ];
  return PARTICIPATING_ROUTES.includes(routeName);
};

export const isOnFoldersView = ({ route: { name: routeName } }) => {
  const FOLDER_ROUTES = [
    'folder_conversations',
    'conversations_through_folders',
  ];
  return FOLDER_ROUTES.includes(routeName);
};

export const buildConversationList = (
  context,
  requestPayload,
  responseData,
  filterType
) => {
  const { payload: conversationList, meta: metaData } = responseData;
  const conversationPrefetchLimit = getPrefetchConversationLimit(
    context.rootGetters?.getCurrentUser
  );
  const sanitizedConversationList = sanitizeConversationListUnreadForViewer(
    conversationList,
    context.rootGetters?.getCurrentUser?.id,
    {
      // Quando o servidor já filtrou por unread_only, o registro precisa manter
      // o contador para a filtragem local e para o card, inclusive em conversas
      // atribuídas a outro usuário (ex.: Pessoais acompanhadas pelo gestor).
      preserveAssignedUnread:
        shouldPreserveAssignedUnreadForBox(requestPayload?.aceleresBox) ||
        requestPayload?.unreadOnly === true,
    }
  );
  const shouldReplaceConversationList = Number(requestPayload?.page || 1) <= 1;
  context.commit(
    shouldReplaceConversationList
      ? types.REPLACE_ALL_CONVERSATION
      : types.SET_ALL_CONVERSATION,
    sanitizedConversationList
  );
  // Aceleres: os contadores globais são carregados pelo endpoint de stats,
  // separado da listagem da caixa Pessoais.
  if (filterType !== 'personal' && !requestPayload?.skipCounts) {
    context.dispatch('conversationStats/set', metaData);
  }
  context.dispatch(
    'conversationLabels/setBulkConversationLabels',
    sanitizedConversationList
  );
  setContacts(context.commit, sanitizedConversationList);
  setPageFilter({
    dispatch: context.dispatch,
    filter: filterType,
    page: requestPayload.page,
    markEndReached: !sanitizedConversationList.length,
  });

  const shouldPrefetch =
    Number(requestPayload?.page) === 1 &&
    context.state?.allConversations?.length;

  if (!shouldPrefetch || isSmallViewport()) {
    context.commit(types.CLEAR_LIST_LOADING_STATUS);
    return Promise.resolve(sanitizedConversationList);
  }

  const loadedConversations = [...context.state.allConversations];
  const initialPrefetchLimit = Math.min(
    conversationPrefetchLimit,
    INITIAL_MESSAGE_PREFETCH_CONVERSATIONS
  );

  if (initialPrefetchLimit <= 0) {
    context.commit(types.CLEAR_LIST_LOADING_STATUS);
    return Promise.resolve(sanitizedConversationList);
  }

  // A lista deve aparecer assim que a API de conversas responder. O prefetch
  // de mensagens e apenas um aquecimento e nao pode bloquear a primeira
  // renderizacao; quando ele demora, o spinner ficava preso ate o timeout.
  context.commit(types.CLEAR_LIST_LOADING_STATUS);
  context
    .dispatch('prefetchRecentConversationMessages', {
      conversations: loadedConversations,
      limit: initialPrefetchLimit,
      ensureReady: false,
    })
    .catch(() => null);

  return Promise.resolve(sanitizedConversationList);
  // Aceleres 2026-07-01: cache stale-while-revalidate no localStorage REMOVIDO
  // (servia lista com até 5min de atraso → "só atualiza com F5"). Ver actions.js.
};
